import {
  MoreVert as MoreVertIcon,
  Pageview as PageViewIcon,
  ZoomIn as ZoomInIcon,
} from "@mui/icons-material";
import {
  Grid,
  SelectChangeEvent,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";
import classnames from "classnames";
import { useEffect, useState } from "react";
import { WorkplaceCultureScale } from "../../components/WorkplaceCultureScale/WorkplaceCultureScale";
import { Card } from "../../components/ui/Card/Card";
import useTranslation from "../../hooks/useTranslation";
import {
  GetSurveyScoringDemographicResponse,
  SurveyChapterInsight,
} from "../../types/workplaceCulture";
import {
  ENGAGEMENT_THRESHOLD,
  MAX_SCALE_VALUE,
  MIN_SCALE_VALUE,
  OVERVIEW_DEMOGRAPHIC_GROUP_ID,
  generateVisibleScores,
  getGroupedDemographicScopes,
  getMinMaxScoreValuesForFilter,
} from "../../utils/WorkplaceCulture.utils";
import { NoDataState } from "../NoDataState/NoDataState";
import { WorkplaceCultureDemographicFilter } from "../WorkplaceCultureDemographicFilter/WorkplaceCultureDemographicFilter";
import { WorkplaceCultureScaleSettings } from "../WorkplaceCultureScaleSettings/WorkplaceCultureScaleSettings";
import { Button } from "../ui/Button/Button";
import { Menu } from "../ui/Menu/Menu";
import { Tooltip } from "../ui/Tooltip/Tooltip";
import "./WorkplaceCultureOverview.scss";

interface WorkplaceCultureOverviewProps {
  hasEnoughAnswers: boolean;
  scoringData?: GetSurveyScoringDemographicResponse;
  isLoading?: boolean;
  error?: boolean;
  insightsData?: SurveyChapterInsight[];
  isWidget?: boolean;
  title?: string;
  selectedDemographicFilter?: string;
  onScoringRefetch?: (filterId: string) => void;
  isInChapter?: boolean;
  surveyDispatchName?: string;
}

export const WorkplaceCultureOverview = ({
  scoringData,
  hasEnoughAnswers = false,
  isLoading = true,
  isWidget = false,
  error = false,
  title,
  selectedDemographicFilter,
  onScoringRefetch,
  isInChapter = false,
  surveyDispatchName = "",
}: WorkplaceCultureOverviewProps) => {
  const { t } = useTranslation("workplaceCulturePage");
  const [selectedDemographicGroups, setSelectedDemographicGroups] = useState([
    OVERVIEW_DEMOGRAPHIC_GROUP_ID,
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [minScaleValue, setMinScaleValue] = useState(MIN_SCALE_VALUE);
  const [maxScaleValue, setMaxScaleValue] = useState(MAX_SCALE_VALUE);
  const [isAutoZoomOn, setIsAutoZoom] = useState(false);
  const [moreActionsAnchor, setMoreActionsAnchor] = useState<HTMLElement>();
  const [hideIsSkip, setHideIsSkip] = useState(true);

  // Make sure that the if auto zoom is selected the scale is updated when filters change
  useEffect(() => {
    if (isAutoZoomOn) {
      handleAutoZoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDemographicGroups]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMoreActionsAnchor(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMoreActionsAnchor(undefined);
  };

  const handleAutoZoom = () => {
    if (!scoringData) {
      return;
    }

    const allScores = generateVisibleScores(
      scoringData.scores,
      MIN_SCALE_VALUE,
      MAX_SCALE_VALUE,
      selectedDemographicGroups,
      scoringData.id,
      isInChapter,
      hideIsSkip,
      t,
      title
    );

    const { minScoreValue, maxScoreValue } =
      getMinMaxScoreValuesForFilter(allScores);

    setMinScaleValue(minScoreValue);
    setMaxScaleValue(maxScoreValue);
    setIsAutoZoom(true);
  };

  const handleResetZoom = () => {
    setMinScaleValue(MIN_SCALE_VALUE);
    setMaxScaleValue(MAX_SCALE_VALUE);
    setIsAutoZoom(false);
  };

  const handleManualZoom = (newValue: number[]) => {
    setMinScaleValue(newValue[0]);
    setMaxScaleValue(newValue[1]);
    setIsAutoZoom(false);
  };

  const handleFilterClick = (filterId: string) => {
    // Don't allow to unselect the first filter
    if (filterId === OVERVIEW_DEMOGRAPHIC_GROUP_ID) {
      setSelectedDemographicGroups([OVERVIEW_DEMOGRAPHIC_GROUP_ID]);
      return;
    }

    if (selectedDemographicGroups.includes(filterId)) {
      const newDemographicGroups = selectedDemographicGroups.filter(
        (id) => id !== filterId
      );

      // If no demographic groups are selected, select the first one
      if (!newDemographicGroups.length) {
        setSelectedDemographicGroups([OVERVIEW_DEMOGRAPHIC_GROUP_ID]);
      } else {
        setSelectedDemographicGroups(newDemographicGroups);
      }
    } else {
      // if selectedDemographicGroups include 0, remove it before adding the new demographicGroup
      let newDemographicGroups = selectedDemographicGroups;
      if (newDemographicGroups.includes(OVERVIEW_DEMOGRAPHIC_GROUP_ID)) {
        newDemographicGroups = newDemographicGroups.filter(
          (id) => id !== OVERVIEW_DEMOGRAPHIC_GROUP_ID
        );
      }
      setSelectedDemographicGroups([...newDemographicGroups, filterId]);
    }
  };

  const handleChangeFilter = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value as string;
    handleResetZoom();
    onScoringRefetch && onScoringRefetch(selectedValue);
  };

  const visibleScores =
    scoringData && hasEnoughAnswers
      ? generateVisibleScores(
          scoringData.scores,
          minScaleValue,
          maxScaleValue,
          selectedDemographicGroups,
          scoringData.id,
          isInChapter,
          hideIsSkip,
          t
        )
      : undefined;

  const demographicFilters =
    scoringData && getGroupedDemographicScopes(scoringData.scores);

  const renderLoading = () => (
    <Skeleton className="WorkplaceCultureOverview__loading" />
  );

  const renderError = () => (
    <NoDataState title={t("scale.error")} info={t("scale.errorInfo")} />
  );

  const renderNotEnoughData = (title: string, info: string) => (
    <NoDataState title={title} info={info} />
  );

  const renderTitle = () => {
    const menuItems = [
      {
        text: hideIsSkip
          ? t("scale.actions.toggleIsSkipOff")
          : t("scale.actions.toggleIsSkipOn"),
        icon: (
          <Switch
            checked={!hideIsSkip}
            onClick={() => setHideIsSkip(!hideIsSkip)}
            data-testid="toggle-is-skip"
          />
        ),
      },
    ];

    return (
      <Grid container className="WorkplaceCultureOverview__title">
        <Grid item md={2}>
          {!error && !isLoading && (
            <WorkplaceCultureDemographicFilter
              demographicFilters={demographicFilters}
              onChange={handleChangeFilter}
              selectedValue={selectedDemographicFilter}
              disabled={isLoading || error}
            />
          )}
        </Grid>
        <Grid item md={8}>
          {title && (
            <>
              <Typography variant="h5" color="secondary" sx={{ mb: 1 }}>
                {t("chapterPage.overview.title")}
              </Typography>
              <Typography variant="handwrittenH2" color="error">
                {title}
              </Typography>
            </>
          )}
          {!title && (
            <>
              <Typography variant="h5" color="secondary" sx={{ mb: 1 }}>
                {surveyDispatchName}
              </Typography>
              <Typography variant="handwrittenH2" color="error" sx={{ mt: 1 }}>
                {t("scale.scaleInfo")}
              </Typography>
            </>
          )}
        </Grid>
        <Grid item md={2} className="WorkplaceCultureOverview__title__actions">
          {!error && !isLoading && (
            <>
              <Tooltip
                title={
                  isAutoZoomOn
                    ? t("scale.actions.resetZoom")
                    : t("scale.actions.autoZoom")
                }
                arrow
              >
                <span>
                  <Button
                    onClick={isAutoZoomOn ? handleResetZoom : handleAutoZoom}
                    data-testid="auto-zoom-inclusion-scale"
                    aria-label="action on inclusion scale"
                    aria-controls="auto-zoom-inclusion-scale"
                    aria-haspopup="true"
                    small
                    icon
                    variant={isAutoZoomOn ? "contained" : "outlined"}
                    color="secondary"
                    className="WorkplaceCultureOverview__title__actions__autoZoom"
                    disabled={!visibleScores}
                  >
                    <PageViewIcon />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={t("scale.actions.zoom")} arrow>
                <span>
                  <Button
                    onClick={handleOpenModal}
                    data-testid="manual-zoom-inclusion-scale"
                    aria-label="action on inclusion scale"
                    aria-controls="manual-zoom-inclusion-scale"
                    aria-haspopup="true"
                    variant="outlined"
                    color="secondary"
                    small
                    icon
                    disabled={!visibleScores}
                  >
                    <ZoomInIcon />
                  </Button>
                </span>
              </Tooltip>
              <Button
                onClick={handleOpenMenu}
                data-testid="more-scale-actions"
                aria-label="more actions on inclusion scale"
                aria-controls="more-scale-actions"
                aria-haspopup="true"
                variant="outlined"
                color="secondary"
                small
                icon
                disabled={!visibleScores}
              >
                <MoreVertIcon />
              </Button>
              <Menu
                id="more-actions-menu"
                anchorEl={moreActionsAnchor}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: 52,
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={!!moreActionsAnchor}
                onClose={handleCloseMenu}
                menuItems={menuItems}
              />
            </>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderFilters = () => {
    if (!hasEnoughAnswers || error || isLoading || !scoringData) {
      return;
    }

    const availableDemographicGroups: {
      id: string;
      label: string;
      hasEnoughAnswers: boolean;
    }[] = [];
    availableDemographicGroups.push({
      id: OVERVIEW_DEMOGRAPHIC_GROUP_ID,
      label: t("scale.filters.overview.title"),
      hasEnoughAnswers: true,
    });

    // filter out demographic groups that have a demographic that matches the selected demographic filter
    const demographicGroups = scoringData?.scores?.demographic_groups.filter(
      (demographicGroup) =>
        demographicGroup.demographics.every(
          (demographic) => demographic.id !== selectedDemographicFilter
        )
    );

    demographicGroups
      ?.sort((a, b) =>
        a.is_combined === b.is_combined ? 0 : a.is_combined ? -1 : 1
      )
      .forEach((demographicGroup, i) => {
        // check if demographics are empty
        const hasEnoughAnswersForFilter =
          !!demographicGroup.demographics?.length;
        availableDemographicGroups.push({
          id: demographicGroup.id,
          label: demographicGroup.label,
          hasEnoughAnswers: hasEnoughAnswersForFilter,
        });
      });

    return (
      <>
        {availableDemographicGroups.map((demographicGroup, i) => {
          return (
            <Button
              data-testid="workplace-culture-overview-filter-button"
              key={demographicGroup.label}
              className="WorkplaceCultureOverview__filterButton"
              onClick={() => handleFilterClick(demographicGroup.id)}
              color={i === 0 ? "primary" : "secondary"}
              small
              variant={
                selectedDemographicGroups.includes(demographicGroup.id)
                  ? "contained"
                  : "outlined"
              }
              disabled={!demographicGroup.hasEnoughAnswers}
            >
              {demographicGroup.label}
            </Button>
          );
        })}
      </>
    );
  };

  const renderScale = () => {
    if (!visibleScores) {
      return renderNotEnoughData(
        t("scale.noData.title"),
        t("scale.noData.info", { count: ENGAGEMENT_THRESHOLD })
      );
    }

    return (
      <>
        {!isWidget && (
          <>
            <Typography color="secondary">{visibleScores.info}</Typography>
            <Typography color="secondary">
              {visibleScores.missingDemographics
                ? t("scale.missingScaleInfo")
                : ""}
            </Typography>
          </>
        )}
        {isWidget && (
          <Typography color="secondary">{t("scale.scaleInfo")}</Typography>
        )}

        <WorkplaceCultureScale
          values={visibleScores.scaleValues}
          average={visibleScores.averageValue}
          className="WorkplaceCultureOverview__scale"
          small={isWidget}
          minValue={minScaleValue}
          maxValue={maxScaleValue}
        />
      </>
    );
  };

  const classes = classnames("WorkplaceCultureOverview", {
    "WorkplaceCultureOverview--widget": isWidget,
    "WorkplaceCultureOverview--noData": !hasEnoughAnswers || error,
  });

  const containerClasses = classnames("WorkplaceCultureOverview--container", {
    "WorkplaceCultureOverview--widgetContainer": isWidget,
  });

  return (
    <Card
      className={classes}
      containerClassName={containerClasses}
      square={isWidget}
    >
      {!isWidget && (
        <>
          {renderTitle()}
          {renderFilters()}
        </>
      )}
      {!isLoading && !error && renderScale()}
      {isLoading && renderLoading()}
      {error && !isLoading && renderError()}
      <WorkplaceCultureScaleSettings
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        value={[minScaleValue, maxScaleValue]}
        handleChange={handleManualZoom}
        onResetZoom={handleResetZoom}
      />
    </Card>
  );
};
